<script setup lang="ts">
import { Plus } from '@element-plus/icons-vue'
import { BoxComponent, ButtonComponent, DatepickerComponent, FlexComponent, TypographyComponent } from '@metapic-apps/shared'
import { ElButton, ElDialog, ElForm, ElFormItem, ElIcon, ElInput, ElNotification, type FormInstance, type FormItemRule } from 'element-plus'
import { reactive, ref } from 'vue'

import { useCreatorCosts } from '@/api/creator-costs.api'
import { formatMoney } from '@/legacy/store/helpFunctions'
import { formatDateTime } from '@/lib/formatters'

type PaymentForm = {
  comment: string
  paymentDate: Date | string
  amount: number | string
}

const props = defineProps<{ creatorId: number }>()

const { oneTimeEarnings, currencyCode, isSaving, addOneTimePayment } = useCreatorCosts()

const dialogOpen = ref(false)
const formRef = ref<FormInstance>()
const emptyForm = (): PaymentForm => ({
  comment: '',
  paymentDate: '',
  amount: '',
})
const form = reactive<PaymentForm>(emptyForm())

const formatPaymentDate = (date: string): string => formatDateTime(date)

const validateAmount: FormItemRule['validator'] = (_rule, value, callback) => {
  if (value === undefined || value === '' || Number.isNaN(Number(value))) {
    return callback(new Error('Please enter an amount'))
  }
  if (Number(value) <= 0) {
    return callback(new Error('Please enter an amount above zero'))
  }
  return callback()
}

const rules: Partial<Record<keyof PaymentForm, FormItemRule[]>> = {
  comment: [{ required: true, message: 'Please enter a title', trigger: 'blur' }],
  paymentDate: [{ required: true, message: 'Please select a payment date', trigger: 'change' }],
  amount: [
    { required: true, message: 'Please enter an amount', trigger: 'blur' },
    { validator: validateAmount, trigger: 'blur' },
  ],
}

const openDialog = () => {
  dialogOpen.value = true
}

const closeDialog = () => {
  dialogOpen.value = false
  Object.assign(form, emptyForm())
  formRef.value?.resetFields()
}

const onSubmit = async () => {
  if (!formRef.value) {
    return
  }
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }
  const success = await addOneTimePayment(props.creatorId, {
    amount: Number(form.amount),
    paymentDate: new Date(form.paymentDate).toISOString(),
    comment: form.comment,
  })
  if (success) {
    closeDialog()
    ElNotification.success('The additional payment has been added')
  } else {
    ElNotification.error({
      message: "Something went wrong and the payment wasn't added",
      duration: 0,
    })
  }
}
</script>
<template>
  <BoxComponent class="additional-payments">
    <FlexComponent :gap="16" justify="space-between" align="center" class="additional-payments__header">
      <TypographyComponent variant="headline-small">Additional payments</TypographyComponent>
      <ButtonComponent variant="accent" @click="openDialog">
        <template #iconLeft>
          <ElIcon>
            <Plus />
          </ElIcon>
        </template>
        Add additional payment
      </ButtonComponent>
    </FlexComponent>
    <FlexComponent v-if="oneTimeEarnings.length > 0" :gap="8" direction="column">
      <div v-for="(earning, index) in oneTimeEarnings" :key="`${earning.paymentDate}-${index}`" class="payment-row">
        <TypographyComponent variant="title-large" class="payment-row__title">
          {{ earning.comment }}
        </TypographyComponent>
        <TypographyComponent variant="body-medium" class="payment-row__amount">
          {{ formatMoney(earning.amount) }}
        </TypographyComponent>
        <TypographyComponent variant="body-medium" class="payment-row__date">
          {{ formatPaymentDate(earning.paymentDate) }}
        </TypographyComponent>
      </div>
    </FlexComponent>
    <FlexComponent v-else justify="center" class="empty-state">
      <TypographyComponent variant="body-large" color="#636363">
        There have been no additional payments yet for this creator.
      </TypographyComponent>
    </FlexComponent>
    <ElDialog v-model="dialogOpen" width="560px" :before-close="closeDialog">
      <template #header>
        <TypographyComponent variant="headline-medium">Add additional payment</TypographyComponent>
      </template>
      <ElForm ref="formRef" :model="form" :rules="rules" label-position="top" hide-required-asterisk class="payment-form">
        <ElFormItem label="Title" prop="comment">
          <ElInput v-model="form.comment" size="large" />
        </ElFormItem>
        <ElFormItem label="Payment date" prop="paymentDate">
          <DatepickerComponent
            v-model="form.paymentDate"
            type="datetime"
            format="YYYY-MM-DD HH:mm"
            date-format="YYYY/MM/DD"
            time-format="HH:mm"
            :disabled-date="() => false"
            class="payment-form__datepicker"
          />
        </ElFormItem>
        <ElFormItem label="Creator earnings" prop="amount">
          <ElInput v-model="form.amount" size="large" placeholder="0.00" class="earnings-input">
            <template #suffix>
              <span class="earnings-input__suffix">{{ currencyCode }}</span>
            </template>
          </ElInput>
        </ElFormItem>
      </ElForm>
      <template #footer>
        <FlexComponent :gap="16" justify="flex-end" align="center">
          <ElButton text @click="closeDialog">Cancel</ElButton>
          <ButtonComponent variant="accent" :is-loading="isSaving" @click="onSubmit">Save</ButtonComponent>
        </FlexComponent>
      </template>
    </ElDialog>
  </BoxComponent>
</template>
<style scoped lang="scss">
.additional-payments {
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.add-icon {
  margin-right: 4px;
}
.payment-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  &__amount {
    text-align: center;
  }
  &__date {
    text-align: right;
  }
}
.empty-state {
  padding: 32px 0;
}
.payment-form {
  padding-top: 8px;
  :deep(.el-form-item__label) {
    font-size: 16px;
    font-weight: 600;
    color: #222222;
  }
  &__datepicker {
    width: 100%;
  }
  :deep(.el-date-editor.el-input) {
    width: 100%;
  }
  :deep(.el-input__wrapper) {
    min-height: 56px;
  }
}
.earnings-input__suffix {
  color: #222222;
  font-weight: 600;
}
</style>
